import { OK, BAD, WARN } from '../../lib/uiTokens';
import { fmt, fmtKc, MESICE_PLNE } from '../../lib/formatters';

/**
 * Čtení dat Výsledovky — verdikt a blok „K dořešení".
 *
 * Bydlí to mimo komponenty, protože obojí kreslí **dva různě hustý** pohled:
 * na desktopu pruh nad tabulkou a panel vpravo, na mobilu hero karta a seznam
 * nad P&L. Kdyby si to každý z nich složil sám, rozejde se to u první změny
 * (přesně jako se rozešel vlastní pruh období ve Výsledovce s `ObdobiNavigator`).
 *
 * Čísla počítá backend (`VysledovkaPrehledService::verdikt`) — tady se z nich
 * jen skládá česká věta a vybírá, co stojí za upozornění.
 */

/** Kolik procent cíle nad ním ještě není nález, ale šum. */
const KPI_TOLERANCE = 1.0;

/**
 * Hlavní věta panelu. Vrací kusy, ne hotový string — číslo a odchylka se
 * zvýrazňují barvou, takže je komponenta musí umět obalit vlastním elementem.
 *
 * @returns {{
 *   uvod: string, ebitda: string, pct: string|null,
 *   delta: {text: string, barva: string}|null,
 *   proc: {pred: string, trzby: {text: string, barva: string}, mezi: string,
 *          tahne: {text: string, barva: string}|null}|null,
 *   zbyva: string|null,
 * }|null}
 */
export function sestavVerdikt(data, rezim) {
    const v = data?.verdikt?.[rezim === 'projekce' ? 'projekce' : 'zauctovano'];
    if (!v) return null;

    const mesic = MESICE_PLNE[(data.mesic ?? 1) - 1] ?? '';
    const jeProj = rezim === 'projekce';
    const uzavreny = !!data.pacing?.uzavreny;
    // Doběhlý měsíc (podíl 1) se chová jako uzavřený, i když ho ještě nikdo
    // nezamkl — „k dnešku" by o něm byla lež.
    const dobehl = uzavreny || (data.pacing?.podil ?? 0) >= 0.999;

    const uvod = jeProj
        ? `${mesic} míří na EBITDA`
        : (dobehl ? `${mesic} skončil na EBITDA` : `K dnešku je zaúčtovaná EBITDA`);

    // Odchylka od plánu. U Zaúčtováno se poměřuje s POMĚRNÝM plánem (jsme
    // uprostřed měsíce), u Projekce s plánem celého měsíce — to už rozhodl backend.
    let delta = null;
    if (v.delta != null) {
        const nad = v.delta >= 0;
        delta = {
            text: `o ${fmtKc(Math.abs(v.delta))} ${nad ? 'nad plánem' : 'pod plánem'}`,
            barva: nad ? OK : BAD,
        };
    }

    // Druhá věta: co ten výsledek táhne. Má smysl jen když je co srovnávat.
    let proc = null;
    if (v.trzby_delta != null) {
        const trzbyNad = v.trzby_delta >= 0;
        proc = {
            pred: 'Tržby jsou o',
            trzby: {
                text: `${fmtKc(Math.abs(v.trzby_delta))} ${trzbyNad ? 'napřed' : 'pozadu'}`,
                barva: trzbyNad ? OK : BAD,
            },
            mezi: v.tahne ? (trzbyNad ? ', ale sní to' : ' a navíc tlačí' ) : '.',
            tahne: v.tahne
                ? {
                    text: `${v.tahne.nazev.toLowerCase()}: +${fmtKc(v.tahne.delta)} nad plánem`,
                    barva: BAD,
                }
                : null,
        };
    }

    return {
        uvod,
        ebitda: fmtKc(v.ebitda),
        pct: v.ebitda_pct != null ? `${v.ebitda_pct.toFixed(1).replace('.', ',')} %` : null,
        delta,
        proc,
        plan: v.ebitda_ref != null ? fmtKc(v.ebitda_ref) : null,
        zbyva: (!uzavreny && v.zbyva_dnu > 0) ? `zbývá ${v.zbyva_dnu} ${dnu(v.zbyva_dnu)}` : null,
        barva: delta ? delta.barva : null,
    };
}

const dnu = (n) => (n === 1 ? 'den' : (n >= 2 && n <= 4 ? 'dny' : 'dní'));

/**
 * Blok „K dořešení" — co v tomhle měsíci nehraje, každá položka s číslem
 * a s tím, co se s ní dá udělat. Seřazené od nejhoršího; vrací max 4 položky,
 * pod pravým sloupcem ani na telefonu není místo na víc a delší seznam přestane
 * být seznam nálezů a začne být další tabulka.
 *
 * @returns {{barva: string, titulek: string, detail: string}[]}
 */
export function sestavNalezy(data, rezim) {
    if (!data) return [];
    const jeProj = rezim === 'projekce';
    const nalezy = [];

    // 1) Co táhne plán dolů — stejný nález, který nese druhá věta verdiktu,
    //    ale tady s tím, kde to hledat.
    const tahne = data.verdikt?.[jeProj ? 'projekce' : 'zauctovano']?.tahne;
    if (tahne) {
        const pct = tahne.delta_pct != null ? ` (${tahne.delta_pct.toFixed(1).replace('.', ',')} %)` : '';
        nalezy.push({
            barva: BAD,
            titulek: `${tahne.nazev} +${fmtKc(tahne.delta)} nad plánem${pct}`,
            detail: tahne.skupina === 'personal'
                ? 'Projekce už počítá s naplánovanými směnami do konce měsíce — škrtat se dá jen v plánu.'
                : 'Největší přetažení plánu v měsíci.',
        });
    }

    // 2) Tržby výrazně pozadu za plánem. Je to nejtěžší položka výsledovky —
    //    když nesedí, nesedí nic pod ní, a plánovaná EBITDA k tomu být nemusí.
    const v = data.verdikt?.[jeProj ? 'projekce' : 'zauctovano'];
    if (v?.trzby_ref > 0 && v.trzby_delta < 0) {
        const schodek = Math.abs(v.trzby_delta) / v.trzby_ref;
        if (schodek >= 0.05) {
            nalezy.push({
                barva: schodek >= 0.15 ? BAD : WARN,
                titulek: `Tržby ${fmtKc(Math.abs(v.trzby_delta))} pod plánem (${Math.round(schodek * 100)} %)`,
                detail: jeProj
                    ? `Projekce ${fmt(v.trzby)} proti plánu ${fmtKc(v.trzby_ref)} — tímhle tempem se měsíc nedožene.`
                    : `Zaúčtováno ${fmt(v.trzby)} proti plánu k dnešku ${fmtKc(v.trzby_ref)}.`,
            });
        }
    }

    // 3) KPI nad cílem. Cíl je z číselníku, ne od oka, takže to je konkrétní zadání.
    const kpiNalez = (kpi, label) => {
        const val = jeProj ? kpi?.projekce_pct : kpi?.skutecnost_pct;
        if (val == null || kpi?.cil == null || val <= kpi.cil + KPI_TOLERANCE) return;
        nalezy.push({
            barva: val > kpi.cil * 1.1 ? BAD : WARN,
            titulek: `${label} ${val.toFixed(1).replace('.', ',')} % proti cíli ${kpi.cil.toFixed(1).replace('.', ',')} %`,
            detail: `Cíl je v číselníku KPI. ${jeProj ? 'Projekce' : 'Zaúčtováno'} přes něj.`,
        });
    };
    kpiNalez(data.kpi?.labor_cost, 'Labor cost');
    kpiNalez(data.kpi?.food_cost, 'Food cost');

    // 4) Sekce, kde chybí doklady — číslo v projekci je odhad z plánu, ne skutečnost.
    (data.sekce || []).forEach(s => {
        const ceka = s.mezisoucet?.ceka_dokladu;
        if (!ceka) return;
        nalezy.push({
            barva: WARN,
            titulek: `${s.nazev}: ${ceka} ${dokladu(ceka)} čeká na zpracování`,
            detail: 'Doklady → Ke schválení. Dokud nejsou, je to číslo odhad z plánu.',
        });
    });

    // 5) Naplánováno, ale nic nezaúčtováno — buď chybí doklady, nebo se neutratí.
    //    Hlásí se jen v druhé polovině měsíce, dřív je to normální.
    if ((data.pacing?.podil ?? 0) >= 0.5) {
        (data.sekce || []).forEach(s => {
            const ms = s.mezisoucet;
            if (!ms?.aop_plan || ms.ceka_dokladu) return;
            if (s.skupina === 'trzby' || s.skupina === 'personal') return;
            const plneni = ms.aop_plan ? ms.skutecnost / ms.aop_plan : null;
            if (plneni == null || plneni >= 0.75) return;
            nalezy.push({
                barva: WARN,
                titulek: `${s.nazev} zaúčtován na ${Math.round(plneni * 100)} % plánu`,
                detail: `${fmt(ms.skutecnost)} z ${fmtKc(ms.aop_plan)}. Buď chybí doklady, nebo se rozpočet neutratí.`,
            });
        });
    }

    // Červené dřív než oranžové, jinak pořadí zachovej.
    const vaha = (n) => (n.barva === BAD ? 0 : 1);
    return nalezy.sort((a, b) => vaha(a) - vaha(b)).slice(0, 4);
}

const dokladu = (n) => (n === 1 ? 'doklad' : (n >= 2 && n <= 4 ? 'doklady' : 'dokladů'));
